import { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Bell, Check, Loader2 } from 'lucide-react';
import { notifyMe } from '@/lib/api';
import { toast } from 'sonner';

const ComingSoonCard = ({ book, index = 0 }) => {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await notifyMe(email, book.id, book.title);
      setDone(true);
      toast.success("You're on the list! We'll notify you at launch.");
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`coming-soon-card-${book.id}`}
    >
      <div className="card-surface overflow-hidden mb-4 relative">
        <span className="absolute top-3 left-3 z-10 bg-gold text-[#050505] text-xs uppercase tracking-widest px-3 py-1 font-medium">
          Coming Soon
        </span>
        <div className="relative aspect-[2/3] overflow-hidden">
          {book.cover_image ? (
            <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-[#0d0d0d] gap-4 px-6 text-center">
              <BookOpen size={40} className="text-gold/40" strokeWidth={1.2} />
              <p className="text-white/40 text-xs uppercase tracking-widest">Cover Reveal Soon</p>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {book.series && <p className="mono text-xs text-gold/70">{book.series}</p>}
        <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
          {book.title}
        </h3>
        <p className="text-sm text-white/50">{book.author}</p>
        {book.release_label && (
          <p className="mono text-sm text-white/60">{book.release_label}</p>
        )}
        {book.short_description && (
          <p className="text-white/40 text-sm leading-relaxed pt-1">{book.short_description}</p>
        )}

        {done ? (
          <div className="flex items-center gap-2 text-gold text-sm pt-3" data-testid={`notify-done-${book.id}`}>
            <Check size={16} />
            We'll email you at launch.
          </div>
        ) : showForm ? (
          <form onSubmit={handleNotify} className="pt-3 space-y-3" data-testid={`notify-form-${book.id}`}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              className="checkout-input"
              data-testid={`notify-email-${book.id}`}
            />
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid={`notify-submit-${book.id}`}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
              Notify Me
            </button>
          </form>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="btn-secondary inline-flex items-center gap-2 mt-3"
            data-testid={`notify-open-${book.id}`}
          >
            <Bell size={16} />
            Notify Me
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default ComingSoonCard;
